import { CreateLoaderTable } from "@/components/Loaders";
import { Main } from "@/components/Main";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { TypePageActions } from "../Plans";
import { TableData } from "@/App/SalesList/Table";
import { getSales, SalesFiltersParams, exportSales } from "@/Api/sales";
import { getSelectSaleTypes } from "@/Api/saleTypes";
import { useAuth } from "@/hooks/auth";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { SalesFiltersComponent, SalesFilters } from "@/components/SalesFilters";
import { getSelectSellers } from "@/Api/sellers";
import { getSelectProductGroups } from "@/Api/productGroups";
import { getSelectClientOrigins } from "@/Api/clientOrigins";
import { getSelectUnities } from "@/Api/unities";
import { getSelectPaymentForms } from "@/Api/paymentForms";
import { getSelectContests } from "@/Api/contests";
import { getSelectProducts } from "@/Api/products";
import { Card, CardContent } from "@/components/ui/card";
import { Mask } from "@/utils/mask";
import { DollarSign, ShoppingCart, TrendingUp, Download, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";

export type TableDataProps = {
  id: number;
  uuid: string;
  unity_id: number;
  client_id: number;
  total: string;
  total_commission_by_quote: number;
  sale_status_id: number;
  sale_type_id: number;
  bank_account_id: number | null;
  sale_code_transaction: string | null;
  tracking_code: string | null;
  document: string | null;
  seller_id: number;
  circuit_route_id: number | null;
  created_by: number | null;
  updated_by?: {
    id: number;
    name: string;
    email: string;
  } | null;
  delivery_address: string | null;
  tenant_id: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  unity: {
    id: number;
    name: string;
  };
  client: {
    id: number;
    uuid: string;
    has_purchase_voucher?: boolean;
    name: string;
    birth_date: string | null;
    gender_id: number;
    observation: string | null;
    phone: string;
    total_balance: number;
    image: string | null;
    email: string | null;
    discount: number;
    discount_type_id: number;
    client_origin_id: number;
    client_category_id: number;
    client_origin?: {
      id: number;
      uuid: string;
      name: string;
    };
    addresses: Array<{
      id: number;
      street: string | null;
      number: string | null;
      complement: string | null;
      neighborhood: string | null;
      city: string | null;
      state: string | null;
      zip_code: string | null;
      latitude: string | null;
      longitude: string | null;
      created_at: string;
      updated_at: string;
      pivot: {
        client_id: number;
        address_id: number;
        is_main: number;
        reference: string | null;
        created_at: string;
        updated_at: string;
      };
    }>;
  };
  transaction_pix_details: Array<any>;
  sale_type: {
    id: number;
    name: string;
  };
  payment_forms: Array<{
    id: number;
    sale_id: number;
    payment_form_id: number;
    value: string;
    number_installments: number | null;
    sale_code_transaction: string | null;
    observation?: string | null;
    payer_name?: string | null;
    date_hour?: string | null;
    payment_advance_id?: number | null;
    created_at: string;
    updated_at: string;
    payment_form: {
      id: number;
      name: string;
    };
  }>;
  process_step_sales: Array<{
    id: number;
    sale_id: number;
    sale_step_id: number;
    process_status_id: number;
    address_id: number | null;
    created_at: string;
    sale_step: {
      id: number;
      name: string;
    };
    process_status: {
      id: number;
      name: string;
    };
    address: any | null;
    process_step_sale_chats: any[];
  }>;
  seller: {
    id: number;
    user_id: number;
    user: {
      id: number;
      name: string;
      email: string;
    };
  };
  products: Array<{
    id: number;
    contest_id: number;
    lottery_id: number;
    product_name: string;
    product_classification_id: number;
    tenant_id: number;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
    pivot: {
      sale_id: number;
      contest_product_classification_id: number;
      quantity: number;
      price_unit: string;
      price_total: string;
      created_at: string;
      updated_at: string;
    };
    product_classification: {
      id: number;
      uuid: string;
      lottery_id: number;
      name: string;
      game_quantity: number;
      dez_quantity: string;
      quota_quantity: number;
      total_without_tariff: number;
      total_with_tariff: number;
      game_percentage: number;
      tariff: number;
      sale_price: number;
      price_without_tariff: number;
      commission_by_quote: number;
      image: string | null;
      created_by_id: number;
      is_active: number;
      product_type_id: number;
      tenant_id: number;
      created_at: string;
      updated_at: string;
      deleted_at: string | null;
    };
    contest: {
      id: number;
      name: string;
      validation_date: string;
      lottery_id: number;
      lottery: {
        id: number;
        name: string;
        color: string;
      };
    };
  }>;
  transactions: Array<{
    id: number;
    uuid: string;
    enterprise_bank_id: number;
    sale_id: number;
    id_transaction: string;
    status: string;
    pix_key: string;
    amount: string;
    emv?: string | null;
    location_id: string;
    url_qr_code: string;
    pix_copy_and_paste: string;
    transaction_type_id: number;
    created_at: string;
    updated_at: string;
  }>;
  circuit_route: {
    id: number;
    title: string;
    starts: string;
    ends: string;
    routes: Array<any>;
  } | null;
  sale_status: {
    id: number;
    name: string;
  };
  bank_account: any | null;
  is_pre_sale?: boolean;
  is_new_client?: boolean;
};

export const PageList = () => {
  const user = useAuth();
  const [searchParams] = useSearchParams();
  const [listData, setListData] = useState({} as any);
  const [state, setState] = useState("loading");
  const isSeller = !!user?.user?.seller?.id;
  // Vendedores não filtram por unidade (podem ter vendas em múltiplas unidades)
  const unityId = isSeller ? null : (user?.user?.unity?.[0]?.id || null);

  const today = Mask.getLocalDate();
  const saleIdFromUrl = searchParams.get('sale_id');
  const DEFAULT_SALE_STATUS_IDS = ["1", "2", "5", "7"];
  const [filters, setFilters] = useState<SalesFilters>(() => {
    // Vendedores não filtram por unidade inicialmente
    const initialUnityId = user?.user?.seller?.id ? null : (user?.user?.unity?.[0]?.id || null);
    if (saleIdFromUrl) {
      return {
        unity_id: initialUnityId,
        sale_id: saleIdFromUrl,
      };
    }
    return {
      unity_id: initialUnityId,
      start_date: today,
      end_date: today,
      date_period: "today",
      sale_status_id: DEFAULT_SALE_STATUS_IDS,
    };
  });
  const [sortConfig, setSortConfig] = useState<{
    column: string;
    direction: "asc" | "desc";
  }>({ column: "created_at", direction: "desc" });
  const [saleTypes, setSaleTypes] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [sellers, setSellers] = useState<
    Array<{ id: number; user: { name: string } }>
  >([]);
  const [lotteries, setLotteries] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [clientOrigins, setClientOrigins] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [unities, setUnities] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [paymentForms, setPaymentForms] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [contests, setContests] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [productClassifications, setProductClassifications] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [isExporting, setIsExporting] = useState(false);
  const { hasPermission } = usePermissions();

  useEffect(() => {
    (async () => {
      try {
        const { data, ok } = await getSelectSaleTypes();
        if (ok) {
          setSaleTypes(data?.response || []);
        }
      } catch (error) {
        console.error("Error fetching sale types:", error);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const { data } = await getSelectSellers({
        unity_id: unityId || filters.unity_id,
        is_active: true,
      });
      setSellers(data?.response || []);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data, ok } = await getSelectProductGroups();
        if (ok) {
          setLotteries(data?.response || []);
        }
      } catch (error) {
        console.error("Error fetching lotteries:", error);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data, ok } = await getSelectClientOrigins();
        if (ok) {
          setClientOrigins(data?.response || []);
        }
      } catch (error) {
        console.error("Error fetching client origins:", error);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data, ok } = await getSelectUnities();
        if (ok) {
          setUnities(data?.response || []);
        }
      } catch (error) {
        console.error("Error fetching unities:", error);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data, ok } = await getSelectPaymentForms();
        if (ok) {
          setPaymentForms(data?.response || []);
        }
      } catch (error) {
        console.error("Error fetching payment forms:", error);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data, ok } = await getSelectContests();
        if (ok) {
          setContests(data?.response || []);
        }
      } catch (error) {
        console.error("Error fetching contests:", error);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data, ok } = await getSelectProducts();
        if (ok) {
          setProductClassifications(data?.response || []);
        }
      } catch (error) {
        console.error("Error fetching product classifications:", error);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setState("loading");
      try {
        const { data } = await getSales(1, {
          unity_id: unityId || filters.unity_id,
          seller_id: user?.user?.seller?.id,
          sale_id: filters.sale_id,
          start_date: filters.start_date,
          end_date: filters.end_date,
          sale_status_id: DEFAULT_SALE_STATUS_IDS,
          order_by: "created_at",
          order_direction: "desc",
        });
        setListData(data?.response);
        setState("production");
      } catch (error) {
        console.error("Error fetching sales:", error);
        setState("production");
      }
    })();
  }, [user?.user?.seller?.id]);

  const pageActions: TypePageActions & { refresh: () => Promise<void>; silentRefresh: () => Promise<void> } = {
    next: async () => {
      if (listData?.current_page < listData?.last_page) {
        setState("loading");
        try {
          const params: SalesFiltersParams = {
            seller_id:
              user?.user?.seller?.id || filters?.seller_id,
            unity_id: unityId || filters.unity_id,
            sale_id: filters.sale_id,
            client_id: filters.client_id,
            client_name: filters.client_name,
            client_origin_id: filters.client_origin_id,
            sale_type_id: filters.sale_type_id,
            payment_form_id: filters.payment_form_id,
            lottery_id: filters.lottery_id,
            contest_id: filters.contest_id,
            process_step_sale_id: filters.process_step_sale_id,
            bank_account_id: filters.bank_account_id,
            sale_status_id: filters.sale_status_id,
            start_date: filters.start_date,
            end_date: filters.end_date,
            per_page: filters?.per_page || 15,
            order_by: sortConfig.column,
            order_direction: sortConfig.direction,
          };
          const { data } = await getSales(listData?.current_page + 1, params);
          setListData(data.response);
        } catch (error) {
          console.error("Error in next pagination:", error);
        } finally {
          setState("production");
        }
      }
      return;
    },
    refresh: async () => {
      setState("loading");
      try {
        const params: SalesFiltersParams = {
          seller_id:
            user?.user?.seller?.id || filters?.seller_id,
          unity_id: unityId || filters.unity_id,
          client_id: filters.client_id,
          client_name: filters.client_name,
          client_origin_id: filters.client_origin_id,
          sale_type_id: filters.sale_type_id,
          payment_form_id: filters.payment_form_id,
          lottery_id: filters.lottery_id,
          contest_id: filters.contest_id,
          process_step_sale_id: filters.process_step_sale_id,
          bank_account_id: filters.bank_account_id,
          sale_status_id: filters.sale_status_id,
          start_date: filters.start_date,
          end_date: filters.end_date,
          per_page: filters?.per_page || 15,
          order_by: sortConfig.column,
          order_direction: sortConfig.direction,
        };
        const { data } = await getSales(listData?.current_page || 1, params);
        setListData(data?.response);
      } catch (error) {
        console.error("Error in refresh:", error);
      } finally {
        setState("production");
      }
    },
    silentRefresh: async () => {
      try {
        const params: SalesFiltersParams = {
          seller_id:
            user?.user?.seller?.id || filters?.seller_id,
          unity_id: unityId || filters.unity_id,
          client_id: filters.client_id,
          client_name: filters.client_name,
          client_origin_id: filters.client_origin_id,
          sale_type_id: filters.sale_type_id,
          payment_form_id: filters.payment_form_id,
          lottery_id: filters.lottery_id,
          contest_id: filters.contest_id,
          process_step_sale_id: filters.process_step_sale_id,
          bank_account_id: filters.bank_account_id,
          sale_status_id: filters.sale_status_id,
          start_date: filters.start_date,
          end_date: filters.end_date,
          per_page: filters?.per_page || 15,
          order_by: sortConfig.column,
          order_direction: sortConfig.direction,
        };
        const { data } = await getSales(listData?.current_page || 1, params);
        setListData(data?.response);
      } catch (error) {
        console.error("Error in silentRefresh:", error);
      }
    },
    previous: async () => {
      if (listData?.current_page > 1) {
        setState("loading");
        try {
          const params: SalesFiltersParams = {
            seller_id:
              user?.user?.seller?.id || filters?.seller_id,
            unity_id: unityId || filters.unity_id,
            sale_id: filters.sale_id,
            client_id: filters.client_id,
            client_name: filters.client_name,
            client_origin_id: filters.client_origin_id,
            sale_type_id: filters.sale_type_id,
            payment_form_id: filters.payment_form_id,
            lottery_id: filters.lottery_id,
            contest_id: filters.contest_id,
            process_step_sale_id: filters.process_step_sale_id,
            bank_account_id: filters.bank_account_id,
            sale_status_id: filters.sale_status_id,
            start_date: filters.start_date,
            end_date: filters.end_date,
            per_page: filters?.per_page || 15,
            order_by: sortConfig.column,
            order_direction: sortConfig.direction,
          };
          const { data } = await getSales(listData?.current_page - 1, params);
          setListData(data.response);
        } catch (error) {
          console.error("Error in previous pagination:", error);
        } finally {
          setState("production");
        }
      }
      return;
    },
    lastPage: async () => {
      if (listData?.current_page !== listData?.last_page) {
        setState("loading");
        try {
          const params: SalesFiltersParams = {
            seller_id:
              user?.user?.seller?.id || filters?.seller_id,
            unity_id: unityId || filters.unity_id,
            sale_id: filters.sale_id,
            client_id: filters.client_id,
            client_name: filters.client_name,
            client_origin_id: filters.client_origin_id,
            sale_type_id: filters.sale_type_id,
            payment_form_id: filters.payment_form_id,
            lottery_id: filters.lottery_id,
            contest_id: filters.contest_id,
            process_step_sale_id: filters.process_step_sale_id,
            bank_account_id: filters.bank_account_id,
            sale_status_id: filters.sale_status_id,
            start_date: filters.start_date,
            end_date: filters.end_date,
            per_page: filters?.per_page || 15,
            order_by: sortConfig.column,
            order_direction: sortConfig.direction,
          };
          const { data } = await getSales(listData?.last_page, params);
          setListData(data?.response);
        } catch (error) {
          console.error("Error in lastPage pagination:", error);
        } finally {
          setState("production");
        }
      }
      return;
    },
  };

  const handleFiltersChange = (newFilters: SalesFilters) => {
    setFilters(newFilters);
  };

  const handleApplyFilters = async () => {
    setState("loading");
    const params: SalesFiltersParams = {
      seller_id:
        user?.user?.seller?.id || filters?.seller_id,
      unity_id: unityId || filters.unity_id,
      sale_id: filters.sale_id,
      client_id: filters.client_id,
      client_name: filters.client_name,
      client_origin_id: filters.client_origin_id,
      sale_type_id: filters.sale_type_id,
      payment_form_id: filters.payment_form_id,
      lottery_id: filters.lottery_id,
      contest_id: filters.contest_id,
      process_step_sale_id: filters.process_step_sale_id,
      bank_account_id: filters.bank_account_id,
      sale_status_id: filters.sale_status_id,
      start_date: filters.start_date,
      per_page: filters?.per_page || 15,
      end_date: filters.end_date,
      start_time: filters.start_time,
      end_time: filters.end_time,
      order_by: sortConfig.column,
      order_direction: sortConfig.direction,
      product_classification_id: filters.product_classification_id,
      product_name: filters.product_name,
      min_total: filters.min_total,
      max_total: filters.max_total,
      is_new_client: filters.is_new_client,
    };
    const { data } = await getSales(1, params);
    setListData(data?.response);
    setState("production");
  };

  const handleClearFilters = async () => {
    const today = Mask.getLocalDate();
    setFilters({
      unity_id: unityId,
      start_date: today,
      end_date: today,
      date_period: "today",
      sale_status_id: DEFAULT_SALE_STATUS_IDS,
    });
    setSortConfig({ column: "created_at", direction: "desc" });
    setState("loading");
    const { data } = await getSales(1, {
      seller_id: user?.user?.seller?.id,
      start_date: today,
      end_date: today,
      sale_status_id: DEFAULT_SALE_STATUS_IDS,
      order_by: "created_at",
      order_direction: "desc",
    });
    setListData(data?.response);
    setState("production");
  };

  const handleSort = async (column: string, direction: "asc" | "desc") => {
    setSortConfig({ column, direction });
    setState("loading");
    const params: SalesFiltersParams = {
      seller_id: user?.user?.seller?.id || filters?.seller_id,
      unity_id: unityId || filters.unity_id,
      sale_id: filters.sale_id,
      client_id: filters.client_id,
      client_name: filters.client_name,
      client_origin_id: filters.client_origin_id,
      sale_type_id: filters.sale_type_id,
      payment_form_id: filters.payment_form_id,
      lottery_id: filters.lottery_id,
      contest_id: filters.contest_id,
      process_step_sale_id: filters.process_step_sale_id,
      bank_account_id: filters.bank_account_id,
      sale_status_id: filters.sale_status_id,
      start_date: filters.start_date,
      end_date: filters.end_date,
      order_by: column,
      per_page: filters?.per_page || 15,
      order_direction: direction,
    };
    const { data } = await getSales(1, params);
    setListData(data?.response);
    setState("production");
  };

  const getExportErrorMessage = async (data: unknown) => {
    const fallback = 'Erro ao exportar vendas';
    if (!(data instanceof Blob)) return fallback;
    try {
      const json = JSON.parse(await data.text());
      return json?.message || fallback;
    } catch {
      return fallback;
    }
  };

  const handleExport = async (format: 'xlsx' | 'csv') => {
    setIsExporting(true);
    try {
      const params: SalesFiltersParams = {
        seller_id: user?.user?.seller?.id || filters?.seller_id,
        unity_id: unityId || filters.unity_id,
        sale_id: filters.sale_id,
        client_id: filters.client_id,
        client_name: filters.client_name,
        client_origin_id: filters.client_origin_id,
        sale_type_id: filters.sale_type_id,
        payment_form_id: filters.payment_form_id,
        lottery_id: filters.lottery_id,
        contest_id: filters.contest_id,
        process_step_sale_id: filters.process_step_sale_id,
        bank_account_id: filters.bank_account_id,
        sale_status_id: filters.sale_status_id,
        start_date: filters.start_date,
        end_date: filters.end_date,
        product_classification_id: filters.product_classification_id,
        product_name: filters.product_name,
        min_total: filters.min_total,
        max_total: filters.max_total,
        is_new_client: filters.is_new_client,
      };

      const response = await exportSales(params, format);

      // Em caso de erro o interceptor devolve `data: {}` (não é um Blob).
      // Sem essa checagem o download gerava um arquivo com "[object Object]".
      if (!response?.ok || !(response.data instanceof Blob)) {
        throw new Error(await getExportErrorMessage(response?.data));
      }

      // Erro da API retornado como JSON dentro do Blob
      if (response.data.type?.includes('application/json')) {
        throw new Error(await getExportErrorMessage(response.data));
      }

      // Criar blob e fazer download
      const blob = new Blob([response.data], {
        type: format === 'csv'
          ? 'text/csv;charset=utf-8;'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vendas_${new Date().toISOString().split('T')[0]}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Exportação ${format.toUpperCase()} concluída com sucesso!`);
    } catch (error) {
      console.error('Erro ao exportar:', error);
      toast.error(error instanceof Error && error.message ? error.message : 'Erro ao exportar vendas');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Main className="xl:max-w-full">
      <If condition={hasPermission("view-sales-list")}>
        <div className="flex items-center justify-between">
          <PageHeader
            icon={ShoppingCart}
            title="Vendas"
            description="Acompanhe todas as vendas realizadas"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                disabled={isExporting || !listData?.data?.length}
                className="gap-2"
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Exportar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('xlsx')} className="gap-2 cursor-pointer">
                <FileSpreadsheet className="h-4 w-4" />
                Exportar Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('csv')} className="gap-2 cursor-pointer">
                <FileText className="h-4 w-4" />
                Exportar CSV (.csv)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex flex-col gap-3">
          <SalesFiltersComponent
            filters={filters}
            sellers={sellers}
            onFiltersChange={handleFiltersChange}
            onApplyFilters={handleApplyFilters}
            onClearFilters={handleClearFilters}
            saleTypes={saleTypes}
            lotteries={lotteries}
            clientOrigins={clientOrigins}
            unities={unities}
            paymentForms={paymentForms}
            contests={contests}
            productClassifications={productClassifications}
            summaryMinTotal={listData?.summary?.min_total}
            summaryMaxTotal={listData?.summary?.max_total}
          />

          {/* Summary Cards */}
          {!state.includes("loading") &&
            listData?.summary?.total_sales !== undefined && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Total em Vendas
                        </p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-2">
                          {Mask.formatCurrency(
                            listData?.summary?.total_sales || 0
                          )}
                        </p>
                      </div>
                      <div className="h-12 w-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                        <DollarSign className="h-6 w-6 text-green-600 dark:text-green-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Quantidade de Vendas
                        </p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-2">
                          {listData?.summary?.total_count || 0}
                        </p>
                      </div>
                      <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                        <ShoppingCart className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Ticket Médio
                        </p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-2">
                          {Mask.formatCurrency(
                            listData?.summary?.middle_ticket || 0
                          )}
                        </p>
                      </div>
                      <div className="h-12 w-12 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center">
                        <TrendingUp className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

          {state === "loading" ? (
            <CreateLoaderTable cols={4} lines={6} />
          ) : (
            <TableData
              dataApi={listData}
              pageActions={pageActions}
              updateFront={setListData}
              onSort={handleSort}
              sortConfig={sortConfig}
            />
          )}
        </div>
      </If>
    </Main>
  );
};
